import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { getInvoicePdfMutation } from '../../graphql/mutations';
import { FaAngleDown, FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { formatBillomatPrice, centsToPrice } from '@/utils/formatter';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from 'reactstrap';
import { InvoiceAdminDetailInfo } from './InvoiceAdminDetailInfo';
import { useSetInvoiceAsPaidMutation } from '../../graphql/mutations/setInvoiceAsPaid.generated';
import { TableCell, TableRow } from '@/components/ui/table';

// Derive { year, month } from an invoice date string. Handles ISO
// (YYYY-MM-DD) dates as well as the German DD.MM.YYYY format.
function getInvoiceYearMonth(
  dateStr?: string | null,
): { year: number; month: number } | null {
  if (!dateStr) return null;
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 };
  }
  const match = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (match) {
    return { year: parseInt(match[3], 10), month: parseInt(match[2], 10) };
  }
  return null;
}

export const InvoiceRow = function ({
  invoice,
  isAdmin,
  toggleHidden,
  currencySymbol,
  isFinanceAndNotAdmin,
  onRefresh,
  storeIntegrationToken,
}: {
  invoice: any;
  isAdmin: boolean;
  toggleHidden: Function;
  currencySymbol: string;
  isFinanceAndNotAdmin?: boolean;
  onRefresh?: () => void;
  storeIntegrationToken?: string | null;
}) {
  const [rowExpanded, setRowExpanded] = useState(false);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [setPaidModalOpen, setSetPaidModalOpen] = useState(false);
  const [paidComment, setPaidComment] = useState('');
  const [totalInCent, setTotalInCent] = useState('');

  const [getInvoicePdf, { loading: loadingInvoice }] = useMutation(
    getInvoicePdfMutation,
  );
  const [setInvoiceAsPaid, { loading: loadingSetPaid }] =
    useSetInvoiceAsPaidMutation();

  const downloadInvoice = async () => {
    const mutationResult = await getInvoicePdf({
      variables: {
        invoice_id: invoice.id,
      },
    });
    const linkSource = `data:application/pdf;base64,${mutationResult.data.getInvoicePdf.file_base64}`;
    const downloadLink = document.createElement('a');
    const fileName = mutationResult.data.getInvoicePdf.filename;
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const downloadBilledContainers = async () => {
    if (!storeIntegrationToken) {
      alert(i18n.t('Invoice:billedContainersTokenMissing'));
      return;
    }
    const yearMonth = getInvoiceYearMonth(invoice.date);
    if (!yearMonth) {
      alert(i18n.t('Invoice:billedContainersNoDate'));
      return;
    }

    setLoadingContainers(true);
    try {
      const response = await fetch('/api/3/Invoices/ChargedContainers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storeIntegrationToken}`,
        },
        body: JSON.stringify({
          Year: yearMonth.year,
          Month: yearMonth.month,
        }),
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `charged_containers_${yearMonth.year}_${String(
        yearMonth.month,
      ).padStart(2, '0')}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '').trim();
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(i18n.t('Invoice:billedContainersError') + ': ' + error.message);
    } finally {
      setLoadingContainers(false);
    }
  };

  const handleSetAsPaid = () => {
    // Pre-fill with parsed amount converted to cents
    const parsedAmount = parseFloat(invoice.parsed_amount || '0');
    const amountInCents = Math.round(parsedAmount * 100);
    setTotalInCent(amountInCents.toString());
    setPaidComment('');
    setSetPaidModalOpen(true);
  };

  const handleConfirmSetPaid = async () => {
    try {
      await setInvoiceAsPaid({
        variables: {
          invoice_id: invoice.id,
          total_in_cent: parseInt(totalInCent),
          comment: paidComment,
        },
      });
      setSetPaidModalOpen(false);
      setPaidComment('');
      setTotalInCent('');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      alert(i18n.t('Invoice:errorSettingPaid') + ': ' + error.message);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>{invoice.invoice_number}</TableCell>
        <TableCell>{invoice.date}</TableCell>
        <TableCell>
          {isAdmin ? (
            <a target="_blank" rel="noopener noreferrer" href={invoice.url}>
              <FaLock /> {invoice.title}
            </a>
          ) : (
            invoice.title
          )}
        </TableCell>
        <TableCell
          style={{
            color:
              invoice.status === 'OPEN' || invoice.status === 'OVERDUE'
                ? '#dc3545'
                : undefined,
            fontWeight:
              invoice.status === 'OPEN' || invoice.status === 'OVERDUE'
                ? 'bold'
                : undefined,
          }}
        >
          {i18n.t('Invoice:invoiceStatus:' + invoice.status)}
        </TableCell>
        {isAdmin && (
          <TableCell className="numbers-formatted">
            {formatBillomatPrice(invoice.total_gross, currencySymbol)}
          </TableCell>
        )}
        <TableCell className="numbers-formatted">
          {formatBillomatPrice(invoice.parsed_amount, currencySymbol)}
        </TableCell>
        <TableCell title={invoice.parsed_payment_direction}>
          {invoice.parsed_payment_direction === null
            ? '-'
            : i18n.t(
                'Invoice:paymentDirection:' + invoice.parsed_payment_direction,
              )}
        </TableCell>
        <TableCell>
          <Button size="sm" onClick={downloadInvoice} disabled={loadingInvoice}>
            {loadingInvoice ? 'loading...' : i18n.t('Invoice:download')}
          </Button>
        </TableCell>
        <TableCell>
          <Button
            size="sm"
            onClick={downloadBilledContainers}
            disabled={loadingContainers || !storeIntegrationToken}
          >
            {loadingContainers
              ? i18n.t('Invoice:loading')
              : i18n.t('Invoice:downloadBilledContainers')}
          </Button>
        </TableCell>
        {isAdmin && (
          <TableCell>
            <Button size="sm" onClick={() => toggleHidden(invoice.id)}>
              {invoice.hidden ? <FaEyeSlash /> : <FaEye />}
            </Button>
          </TableCell>
        )}
        {isAdmin && (
          <TableCell>
            <Button size="sm" onClick={() => setRowExpanded(!rowExpanded)}>
              <FaAngleDown />
            </Button>
          </TableCell>
        )}
        {isFinanceAndNotAdmin && (
          <TableCell>
            <Button
              size="sm"
              color="primary"
              onClick={handleSetAsPaid}
              disabled={loadingSetPaid || invoice.status === 'PAID'}
            >
              {loadingSetPaid
                ? i18n.t('Invoice:loading')
                : i18n.t('Invoice:setAsPaid')}
            </Button>
          </TableCell>
        )}
      </TableRow>
      {rowExpanded && (
        <TableRow>
          <TableCell colSpan={11}>
            <b>Admin info</b>
            {invoice.admin_info ? (
              <InvoiceAdminDetailInfo adminInfo={invoice.admin_info} />
            ) : (
              <p>no admin info available (invoice not synced)</p>
            )}

            {invoice.account_ledger && (
              <div className="mt-3">
                <b>Linked Account Ledger</b>
                <table className="table-sm table-bordered mt-2 table">
                  <tbody>
                    <tr>
                      <th style={{ width: '200px' }}>
                        {i18n.t('AccountLedger:date')}
                      </th>
                      <td>
                        {invoice.account_ledger.booking_date
                          ? new Date(
                              invoice.account_ledger.booking_date,
                            ).toLocaleDateString(
                              i18n.language,
                              getDateFormatOptions(false),
                            )
                          : '-'}
                      </td>
                    </tr>
                    <tr>
                      <th>{i18n.t('AccountLedger:invoiceReference')}</th>
                      <td>{invoice.account_ledger.invoice_reference || '-'}</td>
                    </tr>
                    <tr>
                      <th>{i18n.t('AccountLedger:statementDescriptor')}</th>
                      <td>
                        {invoice.account_ledger.statement_descriptor || '-'}
                      </td>
                    </tr>
                    <tr>
                      <th>{i18n.t('AccountLedger:amount')}</th>
                      <td>
                        {invoice.account_ledger.credit_amount != null
                          ? `- ${formatBillomatPrice(invoice.account_ledger.credit_amount, currencySymbol)}`
                          : invoice.account_ledger.debit_amount != null
                            ? formatBillomatPrice(
                                invoice.account_ledger.debit_amount,
                                currencySymbol,
                              )
                            : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </TableCell>
        </TableRow>
      )}

      {/* Set as Paid Modal */}
      <Modal
        isOpen={setPaidModalOpen}
        toggle={() => setSetPaidModalOpen(false)}
      >
        <ModalHeader toggle={() => setSetPaidModalOpen(false)}>
          {i18n.t('Invoice:setAsPaidTitle')}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <h6>{i18n.t('Invoice:invoiceSummary')}</h6>
            <table className="table-sm table">
              <tbody>
                <tr>
                  <th style={{ width: '150px' }}>
                    {i18n.t('Invoice:invoiceNumber')}
                  </th>
                  <td>{invoice.invoice_number}</td>
                </tr>
                <tr>
                  <th>{i18n.t('Invoice:title')}</th>
                  <td>{invoice.title}</td>
                </tr>
                <tr>
                  <th>{i18n.t('Invoice:invoiceDate')}</th>
                  <td>{invoice.date}</td>
                </tr>
                <tr>
                  <th>{i18n.t('Invoice:amount')}</th>
                  <td>
                    {formatBillomatPrice(invoice.parsed_amount, currencySymbol)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mb-3">
            <Label>{i18n.t('Invoice:totalInCent')}</Label>
            <div className="input-group">
              <Input
                type="text"
                value={totalInCent}
                onChange={(e) =>
                  setTotalInCent(e.target.value.replace(/\D+/, ''))
                }
                placeholder={i18n.t('Invoice:enterAmount')}
              />
              <span className="input-group-text">
                {centsToPrice(parseInt(totalInCent) || 0, currencySymbol)}
              </span>
            </div>
          </div>

          <div>
            <Label>
              {i18n.t('Invoice:comment')} ({i18n.t('Invoice:optional')})
            </Label>
            <Input
              type="textarea"
              value={paidComment}
              onChange={(e) => setPaidComment(e.target.value)}
              placeholder={i18n.t('Invoice:enterComment')}
              rows={3}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSetPaidModalOpen(false)}>
            {i18n.t('Invoice:cancel')}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmSetPaid}
            disabled={!totalInCent || loadingSetPaid}
          >
            {loadingSetPaid
              ? i18n.t('Invoice:loading')
              : i18n.t('Invoice:setAsPaid')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
